import { JobState } from "@eai-toolkit/client";
import {
  ArrowDownward,
  ArrowUpward,
  Clear,
  ErrorOutline,
  FileDownload,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  InputBase,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List as VirtualList,
} from "react-virtualized";

import { JOBS_EVENTS } from "@helpers/events";

import DescriptiveTooltip from "../../../components/DescriptiveTooltip";
import { useDebounce } from "../../../hooks/useDebounce";
import { getJobLogsBlob } from "../api";
import Ellipsis from "../components/Ellipsis";
import type { LocalJob } from "../data/localTypes";
import { useJobLogs } from "../hooks/useJobLogs";

// Delay before a filter change is reported to analytics, so that typing a
// multi-character query produces a single tracked event rather than one per keystroke.
const FILTER_TRACK_DEBOUNCE_MS = 500;

const LogsContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
}));

const LogsHeader = styled(Paper)(({ theme }) => ({
  margin: -1,
  marginBottom: 0,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  padding: `${theme.spacing(2.5)} ${theme.spacing(3)}`,
  display: "flex",
  alignItems: "center",
  zIndex: 11,
}));

const LogsBody = styled("div")({
  flex: 1,
  minHeight: 0,
  position: "relative",
});

const VirtualListContainer = styled("div")({
  height: "100%",
});

const LogRow = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isLast",
})<{ $isLast?: boolean }>(({ theme, $isLast }) => ({
  boxSizing: "border-box",
  display: "grid",
  gridTemplateColumns: `${theme.spacing(9)} minmax(0, 1fr)`,
  columnGap: theme.spacing(2),
  padding: `0 ${theme.spacing(2)}`,
  paddingBottom: $isLast ? theme.spacing(2) : 0,
  fontFamily: '"Courier New", monospace',
  fontSize: "0.875rem",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
}));

const LogsMessage = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(2.5)} ${theme.spacing(3)}`,
}));

const LineNumber = styled("span")(({ theme }) => ({
  display: "inline-block",
  textAlign: "right",
  userSelect: "none",
  color: theme.palette.text.disabled,
}));

const FilterInput = styled(InputBase)(({ theme }) => ({
  background: theme.palette.background.default,
  padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
  borderRadius: theme.spacing(0.5),
  fontSize: "0.875rem",
}));

const LogsActions = styled("div")(({ theme }) => ({
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const StyledSpinner = styled(CircularProgress)(({ theme }) => ({
  marginRight: theme.spacing(3),
}));

const LogsErrorIconTop = styled(ErrorOutline)(({ theme }) => ({
  fontSize: 20,
  marginRight: theme.spacing(3),
  display: "inline-flex",
}));

const FloatingNavigationButton = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "$position",
})<{ $position: "top" | "bottom" }>(({ theme, $position }) => ({
  position: "absolute",
  [$position]: theme.spacing(1),
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 10,
  padding: theme.spacing(0.25),
  display: "flex",
  borderRadius: theme.shape.borderRadius,
}));

const NavigationButton = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
}));

type Props = {
  job: LocalJob;
};

function getLogFilename(job: LocalJob) {
  const fallback = job.id;
  const base = (job.name || fallback)
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${base || fallback}.log`;
}

function JobDetailLogs({ job }: Props) {
  const [downloadingLogs, setDownloadingLogs] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const lastRunId = job.runs?.[job.runs.length - 1]?.id;
  const runningQueueStates: JobState[] = [JobState.QUEUED, JobState.QUEUING];
  const inQueue = runningQueueStates.includes(job.state);
  const { logs, awaitingLogs, logsError } = useJobLogs({
    jobId: job.id,
    runId: lastRunId,
    enabled: !inQueue,
  });

  useEffect(() => {
    // Reset the view for a new run.
    setFilter("");
    setDownloadError(false);
    followingRef.current = true;
    setFollowing(true);
    setIsAtStart(true);
    setIsAtEnd(true);
    currentScrollTopRef.current = 0;
    preFilterScrollTopRef.current = undefined;
    restoreScrollTopRef.current = undefined;
  }, [job.id, lastRunId, inQueue]);

  const handleDownloadLogs = async () => {
    if (!lastRunId || downloadingLogs) {
      return;
    }
    tracker.event(JOBS_EVENTS.LOGS_DOWNLOAD_CLICKED);
    setDownloadingLogs(true);
    setDownloadError(false);
    try {
      const blob = await getJobLogsBlob({ jobId: job.id, runId: lastRunId });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      try {
        link.href = url;
        link.download = getLogFilename(job);
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading job logs:", error);
      setDownloadError(true);
    } finally {
      setDownloadingLogs(false);
    }
  };

  const [filter, setFilter] = useState("");
  const filterLowerCase = filter.toLowerCase();

  const visibleLines = useMemo(
    () =>
      logs
        .map((line, index) => ({ line, number: index + 1 }))
        .filter(({ line }) => line.toLowerCase().includes(filterLowerCase)),
    [logs, filterLowerCase],
  );

  // Filtering itself stays instant; only the analytics event is debounced so we
  // record one event per query the user settles on, not one per keystroke.
  const [debouncedFilter] = useDebounce(filter, FILTER_TRACK_DEBOUNCE_MS);
  useEffect(() => {
    if (debouncedFilter) {
      tracker.event(JOBS_EVENTS.LOGS_FILTER_PERFORMED);
    }
  }, [debouncedFilter]);

  const cacheRef = useRef(
    new CellMeasurerCache({
      defaultHeight: 20,
      fixedWidth: true,
      minHeight: 20,
    }),
  );
  const listRef = useRef<VirtualList | null>(null);
  // Internal list updates can also scroll. Only user input stops following.
  const recentUserScrollRef = useRef(false);
  const pointerDownRef = useRef(false);
  const userScrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const followFrameRef = useRef<number>();
  const followingRef = useRef(true);
  const currentScrollTopRef = useRef(0);
  const preFilterScrollTopRef = useRef<number>();
  const restoreScrollTopRef = useRef<number>();
  const [following, setFollowing] = useState(true);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(true);
  const showEllipsis =
    !awaitingLogs && job.state === JobState.RUNNING && !logsError;
  const rowCount = visibleLines.length + (showEllipsis ? 1 : 0);

  const updateFollowing = (value: boolean) => {
    followingRef.current = value;
    setFollowing(value);
  };

  const updateFilter = (value: string) => {
    if (!filter && value) {
      preFilterScrollTopRef.current = followingRef.current
        ? undefined
        : currentScrollTopRef.current;
    } else if (filter && !value) {
      const scrollTop = preFilterScrollTopRef.current;
      preFilterScrollTopRef.current = undefined;
      if (scrollTop !== undefined) {
        restoreScrollTopRef.current = scrollTop;
        updateFollowing(false);
      }
    }
    setFilter(value);
  };

  useEffect(() => {
    cacheRef.current.clearAll();
    listRef.current?.recomputeRowHeights();
    const restoreScrollTop = restoreScrollTopRef.current;
    if (restoreScrollTop !== undefined) {
      restoreScrollTopRef.current = undefined;
      listRef.current?.scrollToPosition(restoreScrollTop);
    } else if (followingRef.current && rowCount > 0) {
      listRef.current?.scrollToRow(rowCount - 1);
    }
  }, [visibleLines, rowCount]);

  const markUserScroll = () => {
    recentUserScrollRef.current = true;
    clearTimeout(userScrollTimeoutRef.current);
    userScrollTimeoutRef.current = setTimeout(() => {
      recentUserScrollRef.current = false;
    }, 250);
  };

  useEffect(
    () => () => {
      clearTimeout(userScrollTimeoutRef.current);
      if (followFrameRef.current !== undefined) {
        cancelAnimationFrame(followFrameRef.current);
      }
    },
    [],
  );

  const handleListScroll = ({
    clientHeight,
    scrollHeight,
    scrollTop,
  }: {
    clientHeight: number;
    scrollHeight: number;
    scrollTop: number;
  }) => {
    currentScrollTopRef.current = scrollTop;
    const atEnd = scrollHeight - scrollTop - clientHeight <= 4;
    if (atEnd) {
      recentUserScrollRef.current = false;
      if (!followingRef.current) {
        updateFollowing(true);
      }
      return;
    }
    if (
      followingRef.current &&
      (recentUserScrollRef.current || pointerDownRef.current)
    ) {
      updateFollowing(false);
    }
  };

  const measuredWidthRef = useRef<number>();
  const handleListResize = ({ width }: { width: number }) => {
    if (
      measuredWidthRef.current !== undefined &&
      measuredWidthRef.current !== width
    ) {
      cacheRef.current.clearAll();
      listRef.current?.recomputeRowHeights();
      if (followingRef.current && rowCount > 0) {
        listRef.current?.scrollToRow(rowCount - 1);
      }
    }
    measuredWidthRef.current = width;
  };

  const handleRowsRendered = ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => {
    const atEnd = stopIndex >= rowCount - 1;
    setIsAtStart(startIndex === 0);
    setIsAtEnd(atEnd || followingRef.current);
    if (followingRef.current && !atEnd && rowCount > 0) {
      if (followFrameRef.current !== undefined) {
        cancelAnimationFrame(followFrameRef.current);
      }
      // Wrapped rows can change height after rendering. Keep moving to the
      // last row until it is visible.
      followFrameRef.current = requestAnimationFrame(() => {
        listRef.current?.scrollToRow(rowCount - 1);
      });
    }
  };

  const goToStart = () => {
    tracker.event(JOBS_EVENTS.LOGS_GO_TO_START_CLICKED);
    updateFollowing(false);
    setIsAtStart(true);
    setIsAtEnd(false);
    listRef.current?.scrollToRow(0);
  };

  const goToEnd = () => {
    tracker.event(JOBS_EVENTS.LOGS_GO_TO_END_CLICKED);
    updateFollowing(true);
    setIsAtStart(false);
    setIsAtEnd(true);
    listRef.current?.scrollToRow(rowCount - 1);
  };

  return (
    <LogsContainer variant="outlined">
      <LogsHeader variant="outlined">
        {logsError ? (
          <LogsErrorIconTop color="error" />
        ) : (
          <StyledSpinner
            size={20}
            sx={{
              display:
                inQueue || (!awaitingLogs && job.state !== JobState.RUNNING)
                  ? "none"
                  : undefined,
            }}
          />
        )}
        <Typography variant="body1">
          {logsError
            ? "Sorry, we ran into an issue loading the logs"
            : inQueue
              ? "Logs will appear here after job has started"
              : awaitingLogs
                ? job.state === JobState.RUNNING
                  ? "Fetching logs up to now"
                  : "Fetching logs for most recent run"
                : job.state === JobState.RUNNING
                  ? "Updating logs as we receive them"
                  : "Showing all logs for run"}
        </Typography>
        {logs.length > 0 && (
          <LogsActions>
            <Button
              aria-label="Download logs"
              onClick={handleDownloadLogs}
              disabled={downloadingLogs}
              size="small"
              variant="outlined"
              startIcon={
                downloadingLogs ? (
                  <CircularProgress size={16} />
                ) : (
                  <FileDownload fontSize="small" />
                )
              }
            >
              {downloadingLogs ? "Downloading" : "Download logs"}
            </Button>
            <FilterInput
              value={filter}
              onChange={(e) => updateFilter(e.target.value)}
              placeholder="Filter logs"
              inputProps={{ "aria-label": "Filter logs" }}
              startAdornment={
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              }
              endAdornment={
                filter ? (
                  <InputAdornment position="end">
                    <DescriptiveTooltip title="Clear filter">
                      <IconButton
                        aria-label="Clear filter"
                        onClick={() => updateFilter("")}
                        size="small"
                        edge="end"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </DescriptiveTooltip>
                  </InputAdornment>
                ) : null
              }
            />
          </LogsActions>
        )}
      </LogsHeader>

      {logs.length > 0 && visibleLines.length > 0 ? (
        <LogsBody>
          <VirtualListContainer
            onWheelCapture={markUserScroll}
            onPointerDownCapture={() => {
              pointerDownRef.current = true;
            }}
            onPointerUpCapture={() => {
              pointerDownRef.current = false;
            }}
            onPointerCancelCapture={() => {
              pointerDownRef.current = false;
            }}
            onKeyDownCapture={(event) => {
              if (
                [
                  "ArrowUp",
                  "ArrowDown",
                  "PageUp",
                  "PageDown",
                  "Home",
                  "End",
                  " ",
                ].includes(event.key)
              ) {
                markUserScroll();
              }
            }}
          >
            <AutoSizer onResize={handleListResize}>
              {({ width, height }) => (
                <VirtualList
                  ref={listRef}
                  width={width}
                  height={height}
                  rowCount={rowCount}
                  deferredMeasurementCache={cacheRef.current}
                  rowHeight={cacheRef.current.rowHeight}
                  overscanRowCount={10}
                  scrollToIndex={
                    following && rowCount > 0 ? rowCount - 1 : undefined
                  }
                  onScroll={handleListScroll}
                  onRowsRendered={handleRowsRendered}
                  rowRenderer={({ index, key, parent, style }) => (
                    <CellMeasurer
                      key={key}
                      cache={cacheRef.current}
                      columnIndex={0}
                      parent={parent}
                      rowIndex={index}
                    >
                      {index < visibleLines.length ? (
                        <LogRow
                          style={style}
                          data-testid="log-line"
                          $isLast={index === rowCount - 1}
                        >
                          <LineNumber>{visibleLines[index].number}</LineNumber>
                          <span>{visibleLines[index].line}</span>
                        </LogRow>
                      ) : (
                        <LogRow style={style} $isLast={index === rowCount - 1}>
                          <span />
                          <Box>
                            <Ellipsis />
                          </Box>
                        </LogRow>
                      )}
                    </CellMeasurer>
                  )}
                />
              )}
            </AutoSizer>
          </VirtualListContainer>
          {!isAtStart && (
            <FloatingNavigationButton elevation={3} $position="top">
              <NavigationButton
                color="primary"
                onClick={goToStart}
                aria-label="Go to start of logs"
                endIcon={<ArrowUpward />}
              >
                Go to start
              </NavigationButton>
            </FloatingNavigationButton>
          )}
          {!isAtEnd && (
            <FloatingNavigationButton elevation={3} $position="bottom">
              <NavigationButton
                color="primary"
                onClick={goToEnd}
                aria-label="Go to end of logs"
                endIcon={<ArrowDownward />}
              >
                Go to end
              </NavigationButton>
            </FloatingNavigationButton>
          )}
        </LogsBody>
      ) : logs.length > 0 ? (
        <LogsMessage>
          <Typography variant="body2">No lines match your filter</Typography>
        </LogsMessage>
      ) : awaitingLogs ? null : (
        <LogsMessage>
          <Typography variant="body2">
            {job.alive ? "No logs yet" : "No logs"}
          </Typography>
        </LogsMessage>
      )}
      <Snackbar
        open={downloadError}
        autoHideDuration={6000}
        onClose={() => setDownloadError(false)}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setDownloadError(false)}
        >
          Unable to download logs
        </Alert>
      </Snackbar>
    </LogsContainer>
  );
}

export default JobDetailLogs;
